<template>
  <div class="container">
    <!-- Load prompt modal -->
    <div v-if="showLoadPrompt" class="modal-overlay">
      <div class="modal-content">
        <h2>Welcome to KROZ!</h2>
        <p>A saved game was found.</p>
        <button @click="loadSavedGame" class="modal-button">Continue from save</button>
        <button @click="startNewGame" class="modal-button">Start new game</button>
      </div>
    </div>

    <div class="center">
      <nav>
        <button><router-link to="/" style="font-size: 20px;">HOME</router-link></button>&nbsp;
        <button><router-link to="/about" style="font-size: 20px;">ABOUT</router-link></button>&nbsp;
        <button><a @click.prevent="reloadGame" href="#" style="font-size: 20px;">RELOAD</a></button>
      </nav>
    </div>
    <div ref="titleBox"></div>
    <br>
    <div ref="descBox"></div>
    <br>
    <div v-if="inCombat" class="combat-status">
      <strong>⚔️ COMBAT</strong> | Enemy: {{ currentEnemy?.name }} ({{ currentEnemy?.hp }} HP) | 
      Your HP: {{ player.hp }}/{{ player.max_hp }} | Status: {{ player.status }}
    </div>
    <input ref="inputCommand" v-model="command" @keyup.enter="checkCommand" 
      :placeholder="inCombat ? 'attack/defend/flee' : 'Type help for commands...'"
      :disabled="gameOver || showLoadPrompt"
      style="width: 100%;" />
    <br>
    <br>
    <div ref="errorBox" class="errorBox"></div>
    <div ref="infoBox" class="infoBox"></div>
  </div>
</template>

<script>
import * as utils from './utils/utils';
import * as logic from './utils/logic';

export default {
  name: 'GameView',
  data() {
    return {
      command: '',
      chapter: null,
      chapterNum: 1,
      actualRoom: null,
      inventory: [],
      player: utils.getPlayerState(),
      lastItemSeen: null,
      lastSearched: null,
      moveHistory: [],
      inCombat: false,
      currentEnemy: null,
      gameOver: false,
      showLoadPrompt: false,
      titleBox: null,
      descBox: null,
      errorBox: null,
      infoBox: null,
      actionAllowed: false
    };
  },
  mounted() {
    // Check for saved game
    const savedGame = utils.loadGame();
    if (savedGame) {
      this.showLoadPrompt = true;
    } else {
      this.initializeGame();
    }
  },
  methods: {
    initializeGame() {
      this.chapter = utils.getChapter(1);
      this.actualRoom = utils.firstRoom(this.chapter);
      
      this.titleBox = utils.prepareTypedBox(this.$refs.titleBox, [utils.prefix(`CHAPTER 1: ${this.chapter.name}<br/>${this.actualRoom.name}`)]);
      this.descBox = utils.prepareTypedBox(this.$refs.descBox, [utils.prefix(this.chapter.story + "<br/><br/>" + this.actualRoom.description)], 1500);
      this.errorBox = utils.prepareTypedBox(this.$refs.errorBox);
      this.infoBox = utils.prepareTypedBox(this.$refs.infoBox);
    },
    loadSavedGame() {
      this.showLoadPrompt = false;
      const state = utils.loadGame();
      if (state) {
        this.chapterNum = state.chapterNum;
        this.chapter = utils.getChapter(state.chapterNum);
        this.actualRoom = utils.getRoom(this.chapter, state.roomId);
        this.inventory = state.inventory;
        this.player = state.player;
        this.moveHistory = state.moveHistory || [];
        
        this.titleBox = utils.prepareTypedBox(this.$refs.titleBox, [utils.prefix(`CHAPTER ${this.chapterNum}: ${this.chapter.name}<br/>${this.actualRoom.name}`)]);
        this.descBox = utils.prepareTypedBox(this.$refs.descBox, [utils.prefix(this.actualRoom.description)]);
        this.errorBox = utils.prepareTypedBox(this.$refs.errorBox);
        this.infoBox = utils.prepareTypedBox(this.$refs.infoBox, [utils.prefix("Game loaded!")]);
      }
    },
    startNewGame() {
      this.showLoadPrompt = false;
      localStorage.removeItem('kroz_save');
      this.initializeGame();
    },
    reloadGame() {
      // Reset game state without losing saves
      this.gameOver = false;
      this.inCombat = false;
      this.currentEnemy = null;
      this.command = '';
      this.lastItemSeen = null;
      this.lastSearched = null;
      
      // Check for saved game
      const savedGame = utils.loadGame();
      if (savedGame) {
        this.showLoadPrompt = true;
      } else {
        // Reset to chapter 1
        this.chapterNum = 1;
        this.chapter = utils.getChapter(1);
        this.actualRoom = utils.firstRoom(this.chapter);
        this.inventory = [];
        this.player = utils.getPlayerState();
        this.moveHistory = [];
        
        utils.updateTypedBox(this.titleBox, [utils.prefix(`CHAPTER 1: ${this.chapter.name}<br/>${this.actualRoom.name}`)]);
        utils.updateTypedBox(this.descBox, [utils.prefix(this.chapter.story + "<br/><br/>" + this.actualRoom.description)]);
        utils.updateTypedBox(this.errorBox, ['']);
        utils.updateTypedBox(this.infoBox, [utils.prefix("Game reloaded!")]);
      }
    },
    async checkCommand() {
      if (this.gameOver && !this.command.toLowerCase().includes('load')) {
        utils.updateTypedBox(this.errorBox, [utils.prefix("Game Over. Type 'load' to continue.")]);
        this.command = '';
        return;
      }
      
      utils.updateTypedBox(this.infoBox, ['']);
      this.actionAllowed = false;
      
      // Process status effects
      logic.processStatusEffects(this.$data);
      
      // Check commands in order
      if (logic.loadCommands(this.$data)) return;
      if (logic.saveCommands(this.$data)) return;
      if (logic.helpCommands(this.$data)) return;
      if (logic.inventoryCommands(this.$data)) return;
      if (logic.compassCommands(this.$data)) return;
      if (logic.mapCommands(this.$data)) return;
      if (logic.changeChapter(this.$data)) return;
      
      if (this.inCombat) {
        if (logic.combatCommands(this.$data)) return;
      } else {
        if (logic.moveCommands(this.$data)) return;
        if (logic.lookupCommands(this.$data)) return;
        if (logic.getCommands(this.$data)) return;
        if (logic.useCommands(this.$data)) return;
      }
      
      if (!this.actionAllowed) {
        utils.updateTypedBox(this.errorBox, [utils.prefix(utils.commandNotAllowedMsg())]);
      }
      
      this.command = '';
    }
  },
  beforeUnmount() {
    if (this.titleBox) this.titleBox.destroy();
    if (this.descBox) this.descBox.destroy();
    if (this.errorBox) this.errorBox.destroy();
    if (this.infoBox) this.infoBox.destroy();
  }
};
</script>

<style scoped>
.combat-status {
  background: #2a2a2a;
  padding: 10px;
  margin: 10px 0;
  border-left: 4px solid #ff4444;
  font-family: monospace;
  color: #ff4444;
}

.errorBox {
  color: #ff6b6b;
  margin-top: 10px;
}

.infoBox {
  color: #4ecdc4;
  margin-top: 10px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(34, 40, 49, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #222831;
  padding: 30px;
  border: 1px solid darkgray;
  text-align: center;
  max-width: 400px;
  font-family: 'Courier New', Courier, monospace;
}

.modal-content h2 {
  color: darkgray;
  margin-bottom: 20px;
  font-family: 'Courier New', Courier, monospace;
}

.modal-content p {
  color: darkgray;
  margin-bottom: 20px;
  font-family: 'Courier New', Courier, monospace;
}

.modal-button {
  background: transparent;
  color: darkgray;
  border: 1px solid darkgray;
  padding: 10px 20px;
  margin: 5px;
  cursor: pointer;
  font-size: 16px;
  font-family: 'Courier New', Courier, monospace;
}

.modal-button:hover {
  background: darkgray;
  color: #222831;
}
</style>
